/**
 * Debug utilities for testing Okta auth flows.
 * ONLY USE IN DEVELOPMENT - these are exposed on window for console access.
 *
 * Usage (in browser console):
 *   window.debugAuth.expireToken()           // Force access token expired + trigger refresh attempt
 *   window.debugAuth.expireRefreshToken()    // Corrupt refresh token so the /token endpoint rejects renewal (tests redirect fallback)
 *   window.debugAuth.failNextRefresh()       // Make next silent refresh fail (to test warning)
 *   window.debugAuth.getRefreshToken()       // View current refresh token
 *   window.debugAuth.getTokenInfo()          // View current token state
 *   window.debugAuth.simulateRefresh()       // Trigger the redirect flow
 *   window.debugAuth.triggerRefreshAttempt() // Manually trigger the silent refresh check
 *   window.debugAuth.triggerWarning()        // Force show the warning (bypasses refresh attempt)
 */

import constants from '@/constants';

import { setReturnTo } from './auth';

import oktaAuth from '@/okta/oktaConfig';

type RefreshAttemptTrigger = () => void;
let registeredRefreshAttemptTrigger: RefreshAttemptTrigger | null = null;

type WarningTrigger = () => void;
let registeredWarningTrigger: WarningTrigger | null = null;

export const registerRefreshAttemptTrigger = (
  trigger: RefreshAttemptTrigger,
) => {
  registeredRefreshAttemptTrigger = trigger;
};

export const unregisterRefreshAttemptTrigger = () => {
  registeredRefreshAttemptTrigger = null;
};

export const registerWarningTrigger = (trigger: WarningTrigger) => {
  registeredWarningTrigger = trigger;
};

export const unregisterWarningTrigger = () => {
  registeredWarningTrigger = null;
};

import { OktaTokenStorage } from '@/types';

interface DebugAuth {
  cleanState: () => void;
  expireToken: () => void;
  expireRefreshToken: () => void;
  failNextRefresh: () => void;
  getRefreshToken: () => Promise<unknown>;
  getTokenInfo: () => {
    hasToken: boolean;
    expiresAt: number | null;
    expiresIn: number | null;
    isExpired: boolean;
  };
  simulateRefresh: () => Promise<void>;
  triggerRefreshAttempt: () => void;
  triggerWarning: () => void;
}

const debugAuth: DebugAuth = {
  cleanState: () => {
    console.log('[debugAuth] Cleaning corrupted auth state...');
    localStorage.removeItem('okta-token-storage');
    localStorage.removeItem('okta-cache-storage');
    delete (window as { _debugFailNextRefresh?: boolean })
      ._debugFailNextRefresh;
    console.log('[debugAuth] State cleaned. Reloading...');
    location.reload();
  },

  expireToken: () => {
    const storageKey = 'okta-token-storage';
    const storage = localStorage.getItem(storageKey);

    if (!storage) {
      console.warn('[debugAuth] No okta-token-storage found');
      return;
    }

    const parsed = JSON.parse(storage) as OktaTokenStorage;
    if (parsed.accessToken) {
      const expiredAt = Math.floor(Date.now() / 1000) - 30;
      parsed.accessToken.expiresAt = expiredAt;
      localStorage.setItem(storageKey, JSON.stringify(parsed));
      console.log('[debugAuth] Token set to expired in localStorage.');

      if (registeredRefreshAttemptTrigger) {
        console.log('[debugAuth] Triggering silent refresh attempt...');
        registeredRefreshAttemptTrigger();
      } else {
        console.warn(
          '[debugAuth] No refresh trigger registered. Navigate to a page with useSessionExpiryWarning.',
        );
      }
    } else {
      console.warn('[debugAuth] No accessToken found in storage');
    }
  },

  expireRefreshToken: () => {
    const storageKey = 'okta-token-storage';
    const storage = localStorage.getItem(storageKey);

    if (!storage) {
      console.warn('[debugAuth] No okta-token-storage found');
      return;
    }

    const parsed = JSON.parse(storage) as Record<string, unknown> & {
      refreshToken?: { refreshToken?: string; expiresAt?: number };
    };

    if (!parsed.refreshToken?.refreshToken) {
      console.warn(
        '[debugAuth] No refreshToken in storage. Make sure offline_access scope is enabled and re-login.',
      );
      return;
    }

    // Corrupt the refresh token string so Okta's /token endpoint rejects it.
    // This simulates "refresh token revoked/expired" and should trigger the
    // full sign-in redirect fallback in refreshTokensSilently().
    parsed.refreshToken.refreshToken = `invalid-${parsed.refreshToken.refreshToken}`;
    // Also expire the access token so the next API call triggers a renew attempt.
    const accessToken = (parsed as { accessToken?: { expiresAt?: number } })
      .accessToken;
    if (accessToken) {
      accessToken.expiresAt = Math.floor(Date.now() / 1000) - 30;
    }
    localStorage.setItem(storageKey, JSON.stringify(parsed));
    console.log(
      '[debugAuth] Refresh token corrupted and access token expired. Trigger any API call or run triggerRefreshAttempt().',
    );
  },

  failNextRefresh: () => {
    (window as { _debugFailNextRefresh?: boolean })._debugFailNextRefresh =
      true;
    console.log(
      '[debugAuth] Next silent refresh will fail. Run expireToken() or triggerRefreshAttempt() to test.',
    );
  },

  getRefreshToken: async () => {
    const refreshToken = await oktaAuth.tokenManager.get('refreshToken');
    console.log('[debugAuth] Refresh token:', refreshToken);
    return refreshToken;
  },

  getTokenInfo: () => {
    const tokens = oktaAuth.tokenManager.getTokensSync();
    const accessToken = tokens.accessToken;

    if (!accessToken) {
      return {
        hasToken: false,
        expiresAt: null,
        expiresIn: null,
        isExpired: true,
      };
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const expiresIn = accessToken.expiresAt - currentTime;

    return {
      hasToken: true,
      expiresAt: accessToken.expiresAt,
      expiresIn,
      isExpired: expiresIn <= 0,
    };
  },

  simulateRefresh: async () => {
    console.log('[debugAuth] Triggering sign-in redirect...');
    setReturnTo();
    await oktaAuth.signInWithRedirect({ originalUri: window.location.href });
  },

  triggerRefreshAttempt: () => {
    if (registeredRefreshAttemptTrigger) {
      console.log('[debugAuth] Triggering silent refresh attempt...');
      registeredRefreshAttemptTrigger();
    } else {
      console.warn(
        '[debugAuth] No refresh trigger registered. Navigate to a page with useSessionExpiryWarning.',
      );
    }
  },

  triggerWarning: () => {
    if (registeredWarningTrigger) {
      console.log('[debugAuth] Forcing warning display (bypassing refresh)...');
      registeredWarningTrigger();
    } else {
      console.warn(
        '[debugAuth] No warning trigger registered. Navigate to a page with useSessionExpiryWarning.',
      );
    }
  },
};

if (constants.IS_LOCAL_DEPLOYMENT || constants.IS_VERCEL_DEPLOYMENT) {
  (window as unknown as { debugAuth: DebugAuth }).debugAuth = debugAuth;
  console.log(
    '[debugAuth] Debug auth utilities available. Try: window.debugAuth.getTokenInfo()',
  );
}

export default debugAuth;
